import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSubProcessDetails,
  uploadDataIngestionFile,
  sendDataForAudit,
} from "../../../Store/moduleSlice";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiUpload,
  FiFile,
  FiCheckCircle,
  FiAlertCircle,
  FiX,
  FiSave,
  FiFileText,
  FiImage,
  FiFile as FiFilePdf,
  FiFile as FiFileExcel,
  FiArchive,
  FiCalendar,
  FiClock,
  FiShield,
  FiLock,
  FiEye,
} from "react-icons/fi";

const ProcessDetailsPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { moduleName, processName, subProcessName, error, technicalName } =
    location.state || {};
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadedServerFiles, setUploadedServerFiles] = useState({});
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });

  const { subProcessDetails, loading } = useSelector((state) => state.module);

  useEffect(() => {
    if (moduleName && processName && subProcessName) {
      dispatch(
        fetchSubProcessDetails({
          moduleName,
          processName,
          subProcessName,
        }),
      );
    }
  }, [dispatch, moduleName, processName, subProcessName]);

  const formatName = (name) =>
    name
      ?.toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const getFileIcon = (fileName) => {
    const ext = fileName?.toLowerCase().split(".").pop();
    switch (ext) {
      case "pdf":
        return <FiFilePdf className="w-5 h-5 text-red-500" />;
      case "xlsx":
      case "xls":
        return <FiFileExcel className="w-5 h-5 text-green-600" />;
      case "csv":
        return <FiFileExcel className="w-5 h-5 text-blue-600" />;
      case "jpg":
      case "jpeg":
      case "png":
        return <FiImage className="w-5 h-5 text-blue-500" />;
      default:
        return <FiFile className="w-5 h-5 text-gray-500" />;
    }
  };

  const getBankCardStyle = (index) => {
    const banks = [
      {
        name: "HDFC Bank",
        gradient: "from-blue-600 to-blue-700",
        lightBg: "bg-blue-50",
        border: "border-blue-200",
        iconBg: "bg-blue-100",
        text: "text-blue-700",
        pattern: "M0 0 L100 0 L100 100 L0 100 Z",
      },
      {
        name: "HSBC",
        gradient: "from-red-600 to-red-700",
        lightBg: "bg-red-50",
        border: "border-red-200",
        iconBg: "bg-red-100",
        text: "text-red-700",
        pattern: "M0 0 L100 0 L100 100 L0 100 Z",
      },
      {
        name: "ICICI",
        gradient: "from-purple-600 to-purple-700",
        lightBg: "bg-purple-50",
        border: "border-purple-200",
        iconBg: "bg-purple-100",
        text: "text-purple-700",
        pattern: "M0 0 L100 0 L100 100 L0 100 Z",
      },
      {
        name: "IDFC FIRST Bank",
        gradient: "from-emerald-600 to-emerald-700",
        lightBg: "bg-emerald-50",
        border: "border-emerald-200",
        iconBg: "bg-emerald-100",
        text: "text-emerald-700",
        pattern: "M0 0 L100 0 L100 100 L0 100 Z",
      },
    ];
    return banks[index % banks.length];
  };

  const handleFileUpload = async (fileTypeId, event) => {
    const file = event.target.files[0];

    if (!file) return;

    // validate size
    if (file.size > 10 * 1024 * 1024) {
      alert("File size should be less than 10MB");
      return;
    }

    try {
      setUploadProgress((prev) => ({
        ...prev,
        [fileTypeId]: 30,
      }));

      // store local preview file
      setUploadedFiles((prev) => ({
        ...prev,
        [fileTypeId]: file,
      }));

      // upload api call
      const response = await dispatch(
        uploadDataIngestionFile({
          file,
          fileTypeName: fileTypeId,
          subProcess: subProcessName,
          Process: technicalName,
        }),
      ).unwrap();

      setUploadProgress((prev) => ({
        ...prev,
        [fileTypeId]: 100,
      }));

      /*
      store uploaded filename from backend

      example response:
      {
        file: "Cash_flow_HDFC_123.xlsx.enc"
      }
    */

setUploadedServerFiles((prev) => ({
  ...prev,
  [fileTypeId]: response?.items?.filePath,
}));
    } catch (error) {
      console.error(error);

      setUploadProgress((prev) => ({
        ...prev,
        [fileTypeId]: 0,
      }));

      alert("Upload failed");
    }
  };

  const removeFile = (fileTypeId) => {
    setUploadedFiles((prev) => {
      const newState = { ...prev };
      delete newState[fileTypeId];
      return newState;
    });
    setUploadProgress((prev) => {
      const newState = { ...prev };
      delete newState[fileTypeId];
      return newState;
    });
  };

const handleSubmit = async () => {
  if (!dateRange.startDate || !dateRange.endDate) {
    alert("Please select both dates");
    return;
  }

  if (Object.keys(uploadedServerFiles).length === 0) {
    alert("Please upload at least one file");
    return;
  }

  try {
    setSubmitting(true);

    const uploadedData = {};

    Object.entries(uploadedServerFiles).forEach(
      ([key, uploadedFileName]) => {
        uploadedData[key.toUpperCase()] = [
          {
            file: uploadedFileName,
          },
        ];
      }
    );

    const payload = {
      selectedProcess: technicalName,
      selectedSubProcess: subProcessName,
      auditUniverse: moduleName,

      procureToPay: uploadedData,

      dateTime: new Date().toISOString(),

      startDate: new Date(dateRange.startDate).toISOString(),

      endDate: new Date(dateRange.endDate).toISOString(),
    };

    console.log("SEND DATA PAYLOAD", payload);

    await dispatch(sendDataForAudit(payload)).unwrap();

    alert("Audit submitted successfully");

    navigate(-1);
  } catch (error) {
    console.error(error);
    alert("Submit failed");
  } finally {
    setSubmitting(false);
  }
};

  const fileTypes = subProcessDetails?.fileType || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 bg-gray-50 min-h-screen">
        <div className="max-w-4xl mx-auto bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-center gap-3 text-red-600">
            <FiAlertCircle className="w-6 h-6" />
            <span className="font-semibold">Error: {error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="">
        {/* Header Section - Professional Corporate Style */}
        <div className="relative bg-gradient-to-r from-[#36096d] via-[#4a0e8f] to-[#37d5d6] text-white rounded-2xl shadow-1xl border-b-4 border-[#37d5d6] overflow-hidden">
          {/* Animated gradient background with your project colors */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#36096d]/20 via-[#37d5d6]/10 to-[#36096d]/20 animate-pulse"></div>

          {/* Glass morphism effect */}
          <div className="absolute inset-0 backdrop-blur-[2px]"></div>

          <div className="relative p-3 z-10">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-white">
                    {formatName(subProcessName)}
                  </h1>
                  <p className="text-white/80 text-sm mt-1">
                    Upload required documents for {formatName(processName)}
                  </p>
                </div>
              </div>

              <button 
                          onClick={() => navigate(-1)}

              className="group flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#36096d] to-[#37d5d6] rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-white/20">
                <svg
                  className="w-4 h-4 group-hover:-translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back
              </button>
            </div>
          </div>

          {/* Bottom shine effect with your theme colors */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#37d5d6] to-transparent"></div>
        </div>

        {/* Audit Period Section - Premium Date Range Picker */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mt-3">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-[#36096d]/10 rounded-lg">
              <FiCalendar className="w-5 h-5 text-[#36096d]" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Audit Period</h3>
              <p className="text-sm text-gray-500">
                Select the period for audit review
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, startDate: e.target.value })
                  }
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 focus:border-[#36096d] focus:ring-2 focus:ring-[#36096d]/20 transition-all outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, endDate: e.target.value })
                  }
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 focus:border-[#36096d] focus:ring-2 focus:ring-[#36096d]/20 transition-all outline-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Document Upload Section - Bank Cards Style */}
        <div className="mt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {fileTypes.map((item, idx) => {
              const bankStyle = getBankCardStyle(idx);
              const isUploaded = uploadedFiles[item.name];
              const progress = uploadProgress[item.name];

              return (
                <div
                  key={idx}
                  className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-gray-300"
                >
                  {/* Bank Header */}
                  <div
                    className={`bg-gradient-to-r ${bankStyle.gradient} px-3 py-2 relative overflow-hidden`}
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                      <svg viewBox="0 0 100 100" className="w-full h-full">
                        <path d={bankStyle.pattern} fill="white" />
                      </svg>
                    </div>
                    <div className="relative z-10">
                      <h3 className="text-white font-bold text-xl mb-1">
                        {formatName(item.name)}
                      </h3>
                      <p className="text-white/80 text-sm">
                        Upload required audit document
                      </p>
                    </div>
                  </div>

                  {/* Upload Area */}
                  <div className="p-6">
                    {isUploaded ? (
                      // Uploaded File Display
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="p-2 bg-white rounded-lg shadow-sm">
                              {getFileIcon(isUploaded.name)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {isUploaded.name}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {(isUploaded.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <FiCheckCircle className="w-3 h-3 text-green-500" />
                                <span className="text-xs text-green-600">
                                  Uploaded successfully
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFile(item.name)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1"
                          >
                            <FiX className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : progress ? (
                      // Upload Progress
                      <div className="bg-gray-50 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-700">
                            Uploading...
                          </span>
                          <span className="text-sm font-semibold text-[#36096d]">
                            {progress}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-[#36096d] to-[#37d5d6] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      // Upload Zone
                      <label className="cursor-pointer">
                       <input
  type="file"
  className="hidden"
  accept={
    item.name === "AXIS_PDF"
      ? ".pdf"
      : ".pdf,.xlsx,.xls,.csv"
  }
  onChange={(e) => handleFileUpload(item.name, e)}
/>
                        <div className="border-2 border-dashed border-gray-300 hover:border-[#36096d] rounded-xl p-4 text-center transition-all hover:bg-gray-50">
                          <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-[#36096d]/10 transition-colors">
                            <FiUpload className="w-5 h-5 text-gray-500 group-hover:text-[#36096d] transition-colors" />
                          </div>
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Click to upload file
                          </p>
                          <p className="text-xs text-gray-400">
  {item.name === "AXIS_PDF"
    ? "Only PDF supported (Max 10MB)"
    : "PDF, Excel, CSV supported (Max 10MB)"}
</p>
                        </div>
                      </label>
                    )}
                  </div>

                  {/* Status Footer */}
                  <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FiEye className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {isUploaded ? "Ready for review" : "Pending upload"}
                      </span>
                    </div>
                    {isUploaded && (
                      <span className="text-xs text-green-600 font-medium">
                        File is Uploaded
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons - Professional Footer */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 bottom-4 mt-3">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div
                  className={`w-2 h-2 rounded-full ${
                    Object.keys(uploadedServerFiles).length > 0
                      ? "bg-green-500"
                      : "bg-yellow-500"
                  }`}
                ></div>
                <span>
                  {Object.keys(uploadedFiles).length} of {fileTypes.length}{" "}
                  documents uploaded
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setUploadedFiles({});
                  setUploadProgress({});
                }}
                className="px-6 py-2.5 text-gray-600 hover:text-red-600 transition-colors font-medium text-sm"
                disabled={Object.keys(uploadedFiles).length === 0}
              >
                Clear All
              </button>
              <button
                onClick={handleSubmit}
                disabled={
                  Object.keys(uploadedServerFiles).length === 0 ||
                  submitting ||
                  !dateRange.startDate ||
                  !dateRange.endDate
                }
                className={`px-8 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all ${
                  Object.keys(uploadedServerFiles).length > 0 &&
                  !submitting &&
                  dateRange.startDate &&
                  dateRange.endDate
                    ? "bg-gradient-to-r from-[#36096d] to-[#37d5d6] hover:shadow-lg text-white shadow-md hover:scale-105"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }`}
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting for Audit...
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    Submit for Audit Review
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessDetailsPage;
